import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown, type LucideIcon } from "lucide-react";

/**
 * Aramasız açılır liste: CountryPicker'ın panel ve seçenek görünümü, yerel
 * <select> yerine (Windows onu koyu temada açık gri liste olarak çiziyor).
 *
 * WAI-ARIA listbox deseni: tetikleyici düğme paneli açar, odak listeye geçer,
 * ok tuşları seçeneği aria-activedescendant ile gezer.
 */

export interface MenuOption<T extends string> {
  value: T;
  label: string;
  /** Sağda sönük ek bilgi (sayı vb.). */
  meta?: ReactNode;
  /** Değiştiği yerde üstüne grup başlığı çizilir. */
  group?: string;
}

export function MenuSelect<T extends string>({
  value,
  onChange,
  options,
  label,
  icon: Icon,
  panelWidth = 220,
  compact = false,
}: {
  value: T;
  onChange: (value: T) => void;
  options: MenuOption<T>[];
  /** Ekran okuyucu için ad ("Zaman aralığı"); düğmede seçili seçenek yazar. */
  label: string;
  icon?: LucideIcon;
  panelWidth?: number;
  /** Kart başlığı gibi dar yerler için daha alçak düğme. */
  compact?: boolean;
}): ReactNode {
  const id = useId();
  const listId = `${id}-list`;
  const optId = (i: number): string => `${id}-opt-${i}`;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const current = options[selectedIndex];

  const close = (focusTrigger: boolean): void => {
    setOpen(false);
    if (focusTrigger) trigger.current?.focus();
  };
  const choose = (i: number): void => {
    const option = options[i];
    if (option) onChange(option.value);
    close(true);
  };

  // Açılınca etkin seçenek mevcut seçim olur, odak listeye geçer; dışarı tıklama kapatır.
  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, selectedIndex));
    list.current?.focus();
    const onDown = (e: MouseEvent): void => {
      if (!root.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Etkin seçenek görünür alanda kalsın (klavyeyle listenin dibine inildiğinde).
  useEffect(() => {
    if (open) document.getElementById(optId(active))?.scrollIntoView({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const onListKey = (e: ReactKeyboardEvent<HTMLUListElement>): void => {
    const last = options.length - 1;
    const move: Record<string, (i: number) => number> = {
      ArrowDown: (i) => Math.min(last, i + 1),
      ArrowUp: (i) => Math.max(0, i - 1),
      PageDown: (i) => Math.min(last, i + 7),
      PageUp: (i) => Math.max(0, i - 7),
      Home: () => 0,
      End: () => last,
    };
    const step = move[e.key];
    if (step) {
      e.preventDefault();
      setActive(step);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(active);
    } else if (e.key === "Escape" || e.key === "Tab") {
      // Escape açık bir Modal'ı da kapatmasın; Tab'da panel kaybolacağı için odak tetikleyiciye döner.
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  };

  return (
    <div ref={root} className="relative">
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${current?.label ?? ""}`}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`input flex !w-auto cursor-pointer items-center gap-2 text-left ${compact ? "!py-1.5 text-sm" : ""}`}
      >
        {Icon && <Icon size={14} strokeWidth={1.9} aria-hidden className="shrink-0 text-neutral-500" />}
        <span className="whitespace-nowrap">{current?.label}</span>
        <ChevronDown
          size={15}
          strokeWidth={1.9}
          aria-hidden
          className={`shrink-0 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="card animate-modal-in absolute top-full right-0 z-20 mt-1.5 overflow-hidden"
          style={{
            width: `min(${panelWidth}px, calc(100vw - 32px))`,
            background: "var(--color-surface-2)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <ul
            ref={list}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={label}
            aria-activedescendant={optId(active)}
            onKeyDown={onListKey}
            className="max-h-[340px] overflow-y-auto overscroll-contain py-1 outline-none"
          >
            {options.map((o, i) => {
              const isSelected = i === selectedIndex;
              const groupStarts = o.group && o.group !== options[i - 1]?.group;
              return (
                <Fragment key={o.value}>
                  {groupStarts && (
                    <li role="presentation" className="kicker px-3.5 pt-2.5 pb-1">
                      {o.group}
                    </li>
                  )}
                  <li
                    id={optId(i)}
                    role="option"
                    aria-selected={isSelected}
                    onMouseMove={() => i !== active && setActive(i)}
                    onClick={() => choose(i)}
                    className={`mx-1 flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-[7px] text-sm ${
                      i === active ? "bg-white/[0.07] shadow-[inset_2px_0_0_var(--color-accent-400)]" : ""
                    } ${isSelected ? "text-accent-200" : "text-neutral-200"}`}
                  >
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {o.meta !== undefined && (
                      <span className="text-2xs tabular-nums text-neutral-500">{o.meta}</span>
                    )}
                    <span aria-hidden className="grid w-3.5 shrink-0 place-items-center">
                      {isSelected && <Check size={14} strokeWidth={2.2} className="text-accent-400" />}
                    </span>
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
